import { createTheme } from "@mui/material/styles";
import "@mui/x-date-pickers/themeAugmentation";

const MUI_INPUT_HEIGHT = 30;

// App color theme for pickers (matches PerformanceInfoPanel / date picker styling)
const PICKER_ACCENT = "#FBD332";
const PICKER_TEXT = "#242424";
const PICKER_DISABLED = "#CFCFCF";
const PICKER_HOVER_BG = "rgba(251, 211, 50, 0.1)";

export const muiTheme = createTheme({
  typography: {
    fontFamily: '"Inter", sans-serif',
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          height: MUI_INPUT_HEIGHT,
          minHeight: MUI_INPUT_HEIGHT,
          display: "flex",
          alignItems: "center",
          [`& .MuiOutlinedInput-input`]: {
            padding: "4px 14px",
            height: "auto",
            lineHeight: 1.25,
            boxSizing: "border-box",
          },
          [`&.MuiInputBase-sizeSmall .MuiOutlinedInput-input`]: {
            padding: "4px 14px",
          },
          [`&.MuiInputBase-adornedEnd .MuiOutlinedInput-input`]: {
            paddingRight: 32,
          },
        },
        input: {
          padding: "4px 14px",
          lineHeight: 1.25,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        outlined: {
          [`&:not(.MuiInputLabel-shrink)`]: {
            top: "50%",
            transform: "translate(14px, -50%) scale(1)",
          },
          "&.MuiInputLabel-shrink": {
            // Tighter Y offset for 30px inputs + 12px font: reduces gap above top border
            transform: "translate(14px, -5px) scale(0.75)",
          },
          "&.MuiInputLabel-sizeSmall:not(.MuiInputLabel-shrink)": {
            transform: "translate(14px, -50%) scale(1)",
          },
          "&.MuiInputLabel-sizeSmall.MuiInputLabel-shrink": {
            transform: "translate(14px, -5px) scale(0.75)",
          },
        },
      },
    },
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          height: MUI_INPUT_HEIGHT,
          minHeight: MUI_INPUT_HEIGHT,
          display: "flex",
          alignItems: "center",
          fontSize: "12px",
          [`& .MuiInputBase-input`]: {
            padding: "4px 14px",
            height: "auto",
            lineHeight: 1.25,
            fontSize: "12px",
          },
          [`&.MuiInputBase-inputSizeSmall`]: {
            padding: "4px 14px",
          },
          // Match MuiOutlinedInput vertical padding (4px) so picker height aligns with TextField/Select
          [`& .MuiPickersInputBase-sectionsContainer`]: {
            padding: "4px 0",
            fontSize: "12px",
            lineHeight: 1.25,
          },
          [`& .MuiPickersSectionList-sectionContent`]: {
            fontSize: "12px",
            lineHeight: 1.25,
          },
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          height: MUI_INPUT_HEIGHT,
          minHeight: MUI_INPUT_HEIGHT,
          display: "flex",
          alignItems: "center",
          fontSize: "12px",
          // Same horizontal padding as MuiOutlinedInput
          padding: "0 14px",
          // Override MUI X default 8.5px vertical padding (small) so content matches 30px height
          [`& .MuiPickersInputBase-sectionsContainer`]: {
            padding: "4px 0",
            fontSize: "12px",
            lineHeight: 1.25,
          },
          // Adorned end: reserve space for calendar/clock icon like MuiOutlinedInput (32px)
          [`&.MuiInputBase-adornedEnd .MuiPickersInputBase-sectionsContainer`]: {
            paddingRight: 18,
          },
        },
      },
    },
    // Picker action bar (Cancel / OK): black text instead of blue
    MuiPickersLayout: {
      styleOverrides: {
        actionBar: {
          "& .MuiButton-root": {
            color: PICKER_TEXT,
            "&:hover": {
              backgroundColor: PICKER_HOVER_BG,
              color: PICKER_TEXT,
            },
          },
        },
      },
    },
    // Time picker popper: match app color theme (yellow accent, dark text)
    MuiMultiSectionDigitalClockSection: {
      styleOverrides: {
        root: {
          "&:not(:first-of-type)": {
            borderLeftColor: PICKER_DISABLED,
          },
        },
        item: {
          color: PICKER_TEXT,
          fontSize: "12px",
          "&:hover": {
            backgroundColor: PICKER_HOVER_BG,
          },
          "&.Mui-selected": {
            backgroundColor: `${PICKER_ACCENT} !important`,
            color: `${PICKER_TEXT} !important`,
            fontWeight: 600,
            "&:hover": {
              backgroundColor: `${PICKER_ACCENT} !important`,
            },
            "&:focus-visible": {
              backgroundColor: `${PICKER_ACCENT} !important`,
            },
          },
          "&.Mui-focusVisible": {
            backgroundColor: PICKER_HOVER_BG,
          },
          "&.Mui-disabled": {
            color: PICKER_DISABLED,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: MUI_INPUT_HEIGHT,
          height: MUI_INPUT_HEIGHT,
          padding: "0 16px",
        },
        sizeSmall: {
          minHeight: MUI_INPUT_HEIGHT,
          height: MUI_INPUT_HEIGHT,
          padding: "0 12px",
        },
        sizeMedium: {
          minHeight: MUI_INPUT_HEIGHT,
          height: MUI_INPUT_HEIGHT,
        },
        sizeLarge: {
          minHeight: MUI_INPUT_HEIGHT,
          height: MUI_INPUT_HEIGHT,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          width: MUI_INPUT_HEIGHT,
          height: MUI_INPUT_HEIGHT,
          padding: 0,
        },
        sizeSmall: {
          width: MUI_INPUT_HEIGHT,
          height: MUI_INPUT_HEIGHT,
        },
        sizeMedium: {
          width: MUI_INPUT_HEIGHT,
          height: MUI_INPUT_HEIGHT,
        },
        sizeLarge: {
          width: MUI_INPUT_HEIGHT,
          height: MUI_INPUT_HEIGHT,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: 20,
          minHeight: 20,
          maxHeight: 20,
          fontSize: "11px",
          lineHeight: 1.25,
          "& .MuiChip-label": {
            padding: "0 6px",
            fontSize: "11px",
          },
          "& .MuiChip-deleteIcon": {
            fontSize: "14px",
            margin: "0 2px 0 -2px",
          },
        },
        sizeSmall: {
          height: 20,
          minHeight: 20,
          maxHeight: 20,
          "& .MuiChip-label": {
            padding: "0 6px",
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          alignContent: "center",
          paddingTop: 0,
          paddingBottom: 0,
          "& .MuiOutlinedInput-input": {
            padding: "4px 14px",
          },
          "& .MuiAutocomplete-input": {
            alignSelf: "center",
          },
          "& .MuiInputAdornment-root": {
            alignSelf: "center",
          },
          "& .MuiInputAdornment-positionStart": {
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
          },
        },
        tag: {
          margin: "0 4px 0 0",
          alignSelf: "center",
        },
        endAdornment: {
          alignSelf: "center",
        },
      },
    },
  },
});
